<template>
    <div class="video-player-container">
        <video
            ref="videoRef"
            controls
            controlsList="nodownload"
            playsinline
            class="full-width rounded-borders shadow-2"
            :poster="posterUrl"
            @timeupdate="handleTimeUpdate"
            @ended="handleEnded"
            @loadedmetadata="handleLoadedMetadata"
        >
            <source v-if="hlsUrl" :src="hlsUrl" type="application/x-mpegURL" />
            <source :src="streamUrl" type="video/mp4" />
            Your browser does not support playing HTML5 video.
        </video>
        <div
            v-if="props.video.video_codec || props.video.width"
            class="row items-center justify-start q-mt-xs"
        >
            <div class="col-auto">
                <q-btn
                    flat
                    dense
                    no-caps
                    icon="info"
                    :label="showSpecs ? 'Hide Specs' : 'Video Specs'"
                    color="grey-8"
                    size="sm"
                    @click="showSpecs = !showSpecs"
                />
            </div>
        </div>

        <q-slide-transition>
            <div
                v-if="showSpecs"
                class="q-mt-xs q-pa-sm bg-grey-2 rounded-borders text-caption text-grey-9"
            >
                <div class="row q-col-gutter-xs text-center">
                    <div class="col-3">
                        <div class="text-weight-bold">Resolution</div>
                        <div>
                            {{
                                props.video.width && props.video.height
                                    ? `${props.video.width}×${props.video.height}`
                                    : "N/A"
                            }}
                        </div>
                    </div>
                    <div class="col-3">
                        <div class="text-weight-bold">Codecs</div>
                        <div>
                            {{
                                (props.video.video_codec || "-").toUpperCase()
                            }}
                            /
                            {{ (props.video.audio_codec || "-").toUpperCase() }}
                        </div>
                    </div>
                    <div class="col-3">
                        <div class="text-weight-bold">FPS</div>
                        <div>
                            {{
                                props.video.frame_rate
                                    ? `${props.video.frame_rate} fps`
                                    : "N/A"
                            }}
                        </div>
                    </div>
                    <div class="col-3">
                        <div class="text-weight-bold">Size</div>
                        <div>{{ formatFileSize(props.video.file_size) }}</div>
                    </div>
                </div>
            </div>
        </q-slide-transition>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";

const props = defineProps({
    video: {
        type: Object,
        required: true,
    },
    initialPosition: {
        type: Number,
        default: 0,
    },
});

const emit = defineEmits(["watched", "progress-updated"]);

const videoRef = ref(null);
const lastSyncTime = ref(0);
const syncIntervalMs = 3000;
const hasMarkedWatched = ref(false);
const showSpecs = ref(false);

const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const mb = bytes / (1024 * 1024);
    if (mb >= 1000) {
        return (mb / 1024).toFixed(2) + " GB";
    }
    return mb.toFixed(1) + " MB";
};

const streamUrl = computed(() => `/api/videos/${props.video.id}/stream`);
const hlsUrl = computed(() =>
    props.video.hls_path
        ? `/api/videos/${props.video.id}/hls/playlist.m3u8`
        : null,
);
const posterUrl = computed(() =>
    props.video.poster_path ? `/api/videos/${props.video.id}/poster` : null,
);

onMounted(() => {
    if (props.initialPosition > 0 && videoRef.value) {
        videoRef.value.currentTime = props.initialPosition;
    }
});

const handleLoadedMetadata = () => {
    if (props.initialPosition > 0 && videoRef.value) {
        videoRef.value.currentTime = props.initialPosition;
    }
};

const handleTimeUpdate = () => {
    if (!videoRef.value) return;

    const currentTime = Math.floor(videoRef.value.currentTime);
    const duration = videoRef.value.duration;

    if (!duration || isNaN(duration)) return;

    const now = Date.now();
    const progressPct = Math.round((currentTime / duration) * 100);

    // Section 8: Throttled Sync to Backend (Minimum 3 seconds interval)
    if (now - lastSyncTime.value >= syncIntervalMs) {
        lastSyncTime.value = now;
        syncProgressToServer(currentTime, progressPct);
    }

    if (progressPct > 95 && !hasMarkedWatched.value) {
        hasMarkedWatched.value = true;
        syncProgressToServer(currentTime, 100);
        emit("watched", props.video);
    }
};

const handleEnded = () => {
    if (!videoRef.value) return;

    const currentTime = Math.floor(videoRef.value.currentTime);
    hasMarkedWatched.value = true;
    syncProgressToServer(currentTime, 100);
    emit("watched", props.video);
};

const syncProgressToServer = async (position, progress) => {
    try {
        const response = await axios.post(
            `/api/videos/${props.video.id}/progress`,
            {
                position,
                progress,
            },
        );
        emit("progress-updated", response.data.progress);
    } catch (e) {
        // Silently swallow transient sync network errors
    }
};
</script>

<style scoped>
.video-player-container {
    max-width: 100%;
    margin: 0 auto;
}
video {
    max-height: 500px;
    background-color: #000;
}
</style>
